import React from 'react';
import { XCircle, Truck, Info } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Progress } from '../ui/progress';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { deliveryStatusBadge } from './lotStatusStyles';

const calculateProgress = (lot) => {
  if (!lot || !lot.quantity) return 0;
  const total = (lot.good_quantity || 0) + (lot.scrap_quantity || 0) + (lot.rework_quantity || 0);
  return Math.min(100, Math.round((total / lot.quantity) * 100));
};

export const LotOverviewCards = ({
  lot,
  summary,
  hasWorkCycle,
  simplifiedActive,
  hasRole,
  onToggleSimplified,
  onGoToNcTab,
}) => {
  const progressPercent = calculateProgress(lot);

  return (
    <>
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-5">
            <p className="text-xs text-slate-500 uppercase tracking-wider font-bold">Qtà Pianificata</p>
            <p className="text-3xl font-bold text-slate-900 mt-1">{lot.quantity}</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-emerald-500">
          <CardContent className="p-5">
            <p className="text-xs text-emerald-600 uppercase tracking-wider font-bold">Qtà Buona</p>
            <p className="text-3xl font-bold text-emerald-600 mt-1">{lot.good_quantity || 0}</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-red-500">
          <CardContent className="p-5">
            <p className="text-xs text-red-600 uppercase tracking-wider font-bold">Qtà Scarti</p>
            <p className="text-3xl font-bold text-red-600 mt-1">{lot.scrap_quantity || 0}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-5">
            <p className="text-xs text-slate-500 uppercase tracking-wider font-bold">Avanzamento</p>
            <div className="mt-2">
              <Progress value={progressPercent} className="h-3" />
              <p className="text-lg font-bold text-slate-700 mt-1">{progressPercent}%</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Delivery Summary */}
      <Card className="border-l-4 border-l-sky-500" data-testid="delivery-summary-card">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Truck className="w-5 h-5 text-sky-600" />
              Riepilogo Consegne
            </CardTitle>
            {deliveryStatusBadge(summary.delivery_status)}
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div data-testid="summary-ordered">
              <p className="text-xs text-slate-500 uppercase tracking-wider font-bold">Ordinato</p>
              <p className="text-2xl font-bold text-slate-900 mt-1">{summary.ordered_quantity}</p>
            </div>
            <div data-testid="summary-produced">
              <p className="text-xs text-emerald-600 uppercase tracking-wider font-bold">Prodotto</p>
              <p className="text-2xl font-bold text-emerald-600 mt-1">{summary.produced_quantity}</p>
            </div>
            <div data-testid="summary-delivered">
              <p className="text-xs text-sky-600 uppercase tracking-wider font-bold">Consegnato</p>
              <p className="text-2xl font-bold text-sky-600 mt-1">{summary.delivered_quantity}</p>
            </div>
            <div data-testid="summary-remaining">
              <p className="text-xs text-amber-600 uppercase tracking-wider font-bold">Residuo</p>
              <p className="text-2xl font-bold text-amber-600 mt-1">{summary.remaining_quantity}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Simplified Progress Mode Banner & Toggle */}
      <div className="flex items-start gap-4 p-4 bg-amber-50 border-2 border-amber-200 rounded-lg" data-testid="simplified-mode-banner">
        <Info className="w-6 h-6 text-amber-600 shrink-0 mt-0.5" />
        <div className="flex-1">
          <p className="font-bold text-amber-900">
            {!hasWorkCycle
              ? 'Nessun flusso prodotto configurato. Avanzamento semplificato attivo.'
              : (simplifiedActive
                  ? 'Modalita Avanzamento Semplificato attiva.'
                  : 'Modalita Avanzamento Standard attiva (ciclo di lavoro disponibile).')}
          </p>
          <p className="text-amber-700 text-sm mt-1">
            {!hasWorkCycle
              ? 'Registra manualmente i pezzi prodotti e consegnati. Le consegne aggiornano automaticamente lo stato di evasione della riga ordine.'
              : "Puoi alternare tra avanzamento standard (per fasi) e semplificato (manuale) tramite l'interruttore."}
          </p>
        </div>
        {hasWorkCycle && hasRole('admin', 'quality', 'production') && (
          <div className="flex items-center gap-2">
            <Label htmlFor="simplified-toggle" className="text-sm font-semibold text-amber-900">Semplificato</Label>
            <Switch
              id="simplified-toggle"
              checked={!!lot?.use_simplified_progress}
              onCheckedChange={onToggleSimplified}
              data-testid="simplified-toggle"
            />
          </div>
        )}
      </div>

      {/* Open NCs Alert Banner */}
      {lot.open_nc_count > 0 && (
        <div className="flex items-center gap-4 p-4 bg-red-50 border-2 border-red-300 rounded-lg" data-testid="open-nc-alert">
          <XCircle className="w-7 h-7 text-red-600 shrink-0" />
          <div className="flex-1">
            <p className="font-bold text-red-800 text-base">
              {lot.open_nc_count} Non Conformità Apert{lot.open_nc_count === 1 ? 'a' : 'e'}
            </p>
            <p className="text-red-600 text-sm">
              Costo totale NC: €{(lot.total_nc_cost || 0).toFixed(2)}
              {lot.nok_control_count > 0 && ` — ${lot.nok_control_count} controll${lot.nok_control_count === 1 ? 'o' : 'i'} NOK rilevat${lot.nok_control_count === 1 ? 'o' : 'i'}`}
            </p>
          </div>
          <Button
            variant="destructive"
            className="h-11 px-5"
            onClick={onGoToNcTab}
            data-testid="alert-view-ncs"
          >
            Vedi NC
          </Button>
        </div>
      )}

      {/* Lot Info */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Dettagli Lotto</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-wider">Cliente</p>
              <p className="font-medium" data-testid="lot-client-name">{lot.client_name || '-'}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-wider">Ordine Evotech</p>
              <p className="font-mono">{lot.order_number || '-'}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-wider">Ordine Cliente</p>
              <p className="font-mono" data-testid="lot-customer-order-ref">{lot.customer_order_ref || '-'}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-wider">Prodotto</p>
              <p className="font-mono">{lot.product_code || '-'}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-wider">Commessa Evotech</p>
              <p className="font-mono font-semibold">{lot.lot_code || '-'}</p>
            </div>
            <div className="rounded-md bg-sky-50 border border-sky-200 px-2 py-1.5">
              <p className="text-xs text-sky-700 uppercase tracking-wider font-bold">Lotto Cliente</p>
              <p className="font-mono font-bold text-sky-900" data-testid="lot-customer-lot">{lot.customer_lot_snapshot || '-'}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-wider">Revisione</p>
              <p className="font-mono">{lot.revision_code || '-'}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-wider">Configurazione</p>
              <p className="font-mono">{lot.configuration_code || '-'}</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Info Produzione</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-wider">Data Apertura</p>
              <p>{lot.opening_date}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-wider">Data Chiusura</p>
              <p>{lot.closing_date || '-'}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-wider">Operatore</p>
              <p>{lot.responsible_operator || '-'}</p>
            </div>
          </CardContent>
        </Card>

        <Card className={lot.nok_control_count > 0 || lot.open_nc_count > 0 ? 'border-red-200 bg-red-50/30' : ''}>
          <CardHeader>
            <CardTitle className="text-base">Riepilogo Qualità</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-xs text-slate-500 uppercase tracking-wider">Controlli</p>
              <div className="flex items-center gap-2">
                <Badge className="bg-emerald-500 text-white">{(lot.total_control_count || 0) - (lot.nok_control_count || 0)} OK</Badge>
                {lot.nok_control_count > 0 && (
                  <Badge className="bg-red-600 text-white">{lot.nok_control_count} NOK</Badge>
                )}
              </div>
            </div>
            <div className="flex items-center justify-between">
              <p className="text-xs text-slate-500 uppercase tracking-wider">NC Aperte</p>
              <p className={`font-bold text-lg ${lot.open_nc_count > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                {lot.open_nc_count || 0}
              </p>
            </div>
            <div className="flex items-center justify-between">
              <p className="text-xs text-slate-500 uppercase tracking-wider">Costo NC</p>
              <p className={`font-bold text-lg ${lot.total_nc_cost > 0 ? 'text-red-600' : 'text-slate-400'}`}>
                €{(lot.total_nc_cost || 0).toFixed(2)}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  );
};
